/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import axios, { type AxiosError, type AxiosInstance } from 'axios';

import { reportRateLimit } from './reportRateLimit';
import { parseRetryAfterMs } from './retryAfter';

/**
 * Internal. Also read by the fake-429 simulator, so what it produces cannot drift
 * from what this looks for — a simulation the interceptor ignores would prove nothing
 * while looking like a passing verification.
 */
export const RATE_LIMIT_STATUS = 429;

/**
 * Which instances carry the interceptor, and the id to eject it with.
 *
 * A plain `Map` rather than a `WeakMap` so the whole set can be read: the
 * mounted-but-inert dev warning has to know whether anything is registered at all,
 * and the testing reset has to be able to eject every registration. The strong
 * references cost nothing in practice — services register module-level axios
 * singletons once at startup, and those live as long as the app does.
 */
const registeredInstances = new Map<AxiosInstance, number>();

export interface RateLimitInterceptorOptions {
    /**
     * Decides whether a given 429 should raise the notice. Every 429 raises it by
     * default.
     *
     * Use this to narrow or veto — an endpoint that already explains a rate limit
     * with its own UI would otherwise produce two messages, and a service wanting
     * its own kill switch can branch here.
     */
    shouldTrigger?: (error: AxiosError) => boolean;
}

/**
 * Registers an axios response interceptor that raises the standard rate-limit
 * notice whenever a request comes back 429.
 *
 * Axios interceptors are per-instance — `axios.create()` does NOT inherit them
 * from the default export. Calling this with no arguments covers the bare default
 * instance (`axios.get(...)`), which most calls on this platform use. A service
 * with its own instance (e.g. `campingAxios`, `availabilityAlertAxios`) must
 * register that instance as well.
 *
 * The error is always re-rejected, so per-call error handling is unaffected.
 * Registering the same instance twice is a no-op.
 *
 * @returns the axios interceptor id
 */
export function registerRateLimitInterceptor(
    instance: AxiosInstance = axios,
    { shouldTrigger }: RateLimitInterceptorOptions = {}
): number {
    const existingId = registeredInstances.get(instance);
    if (existingId !== undefined) {
        return existingId;
    }

    const interceptorId = instance.interceptors.response.use(
        null,
        (error: AxiosError) => {
            const isRateLimited =
                error.response?.status === RATE_LIMIT_STATUS &&
                (shouldTrigger === undefined || shouldTrigger(error));

            if (isRateLimited) {
                const retryAfter = error.response?.headers?.['retry-after'];
                reportRateLimit(parseRetryAfterMs(retryAfter));
            }

            return Promise.reject(error);
        }
    );

    registeredInstances.set(instance, interceptorId);

    return interceptorId;
}

/**
 * Removes a previously registered interceptor. Mainly for tests and for apps that
 * need to opt out after the fact.
 */
export function unregisterRateLimitInterceptor(instance: AxiosInstance = axios) {
    const interceptorId = registeredInstances.get(instance);
    if (interceptorId === undefined) {
        return;
    }

    instance.interceptors.response.eject(interceptorId);
    registeredInstances.delete(instance);
}

/**
 * Internal. True once any axios instance carries the interceptor, which is what
 * tells the notice apart from a notice nothing can ever raise.
 */
export function hasRateLimitInterceptor() {
    return registeredInstances.size > 0;
}

/**
 * Internal. Reached by tests through the module's `testing` entry point.
 * Registrations are module-level, so an instance registered by one test would
 * otherwise still count as detection for the next one.
 */
export function unregisterAllRateLimitInterceptors() {
    registeredInstances.forEach((interceptorId, instance) => {
        instance.interceptors.response.eject(interceptorId);
    });
    registeredInstances.clear();
}
